'use client'

import { ApolloLink, ServerError, ServerParseError, HttpLink, fromPromise } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import {
  NextSSRApolloClient,
  NextSSRInMemoryCache,
  SSRMultipartLink,
  ApolloNextAppProvider,
} from '@apollo/experimental-nextjs-app-support/ssr'
import * as Sentry from '@sentry/react'
import { SentryLink } from 'apollo-link-sentry'

import { AuthRepository, CommonRepository } from 'src/repositories'
import { Routes } from 'src/routes'

export const httpLink = new HttpLink({
  uri: process.env.GraphQLUrl,
})

const refreshAndSaveToken = async () => {
  const refreshToken = await AuthRepository.loadRefreshToken()
  const username = await AuthRepository.loadUsername()

  const isServer = typeof window === 'undefined'

  if (!refreshToken || !username) {
    console.log('username: ', username)

    // 既存のトークンが取得出来なかった場合
    await CommonRepository.removeAllCookies()
    if (!isServer) {
      Sentry.captureMessage('refreshToken・username どちらかの取得に失敗しました')
      window.location.href = Routes.SignIn.path
    }
    return
  }

  try {
    // トークン再取得保存後、新しく取得したトークンを呼び出し元に返す
    return await AuthRepository.refreshAndSaveTokens({ refreshToken, username })
  } catch (error) {
    console.log('error: ', error)
    Sentry.captureMessage('アクセストークンのリフレッシュ処理に失敗しました')
    // トークンリフレッシュに失敗した場合の処理
    await CommonRepository.removeAllCookies()
    if (!isServer) {
      window.location.href = Routes.SignIn.path
    }
  }
}

// GraphQLでエラーが発生した場合
export const onErrorLink = onError(
  ({ response, networkError, graphQLErrors, operation, forward }) => {
    // networkErrorだった場合、エラー状況に応じてstatusCodeの格納先が異なる
    let parseErrorStatusCode: number | undefined
    let serverErrorStatusCode: number | undefined
    if (networkError?.name === 'ServerParseError' || networkError?.name === 'ServerError') {
      parseErrorStatusCode = (networkError as ServerParseError).statusCode
      serverErrorStatusCode = (networkError as ServerError).statusCode
    }

    const statusCode = (parseErrorStatusCode || serverErrorStatusCode) as number

    // トークンが無効な場合は再取得してリクエストを再実行する
    // 参考: https://stackoverflow.com/questions/61327448/how-to-refresh-jwt-token-using-apollo-and-graphql
    if (statusCode === 401) {
      return fromPromise(refreshAndSaveToken())
        .filter(Boolean)
        .flatMap(updatedTokens => {
          const oldHeaders = operation.getContext().headers
          const newAccessToken = updatedTokens?.accessToken

          operation.setContext({
            headers: { ...oldHeaders, 'bearer-token': newAccessToken ?? '' },
          })
          // 再度リクエストする
          return forward(operation)
        })
    }

    if (graphQLErrors) {
      for (const { extensions, message } of graphQLErrors) {
        // ログイン系の処理を実施しメールアドレスを誤った場合。エラーとして扱わない
        if (message === 'record not found') {
          break
        }

        // ユーザーが存在しない場合は強制的にログイン画面に遷移させる
        if (message === 'UserNotFoundException') {
          // eslint-disable-next-line no-await-in-loop
          // await CommonRepository.removeAllCookies()
          window.location.href = Routes.SignIn.path
          break
        }

        // それ以外のエラーの場合Sentryに送信する
        Sentry.captureException(`graphQLErrors: ${message}`, {
          extra: {
            statusCode: extensions?.status,
            operationName: operation.operationName,
            inputs: {
              ...operation.variables?.input,
            },
          },
        })
      }
    }
    if (networkError) {
      Sentry.captureException(networkError)
    }

    // 401が発生しトークンのリフレッシュ処理を行った後、もしくはトークンのリフレッシュ処理時に、GraphQLを実行して401（認証エラー）が含まれる場合
    const hasAuthenticationExpired = response?.errors?.some(
      error => error.extensions?.status === 401
    )
    if (hasAuthenticationExpired) {
      CommonRepository.removeAllCookies().then(() => {
        // クライアント側の場合、画面が一瞬表示されるため不格好ではあるものの念のためログイン画面に遷移させる
        // すでにログイン画面の場合は何もしない
        const isServer = typeof window === 'undefined'
        const signInPagePaths: string[] = [
          Routes.SignIn.path,
          Routes.SignInPassword.path,
          Routes.IdPasswordSignIn.path,
        ]
        const isSignInPath = signInPagePaths.includes(window.location.pathname)
        // // ログイン周りのページの場合はリダイレクトしない
        if (!isServer && !isSignInPath) {
          window.location.href = Routes.SignIn.path
        }
      })
    }
  }
)

// GraphQLの処理実行前に呼ばれる
const authLink = setContext(async (request, { headers }) => {
  // アクセストークンがある場合はheaderに設定してアクセスする
  const accessToken = AuthRepository.loadAccessToken()
  if (accessToken && accessToken !== 'undefined') {
    return {
      headers: { ...headers, 'bearer-token': accessToken },
    }
  }

  const refreshToken = await AuthRepository.loadRefreshToken()
  const username = await AuthRepository.loadUsername()

  // リフレッシュに必要な情報（refreshToken、username）がない場合はログインしていない状態にする
  if (!refreshToken || !username) {
    return {
      headers: { ...headers, 'bearer-token': '' },
    }
  }

  // アクセストークンがなく、リフレッシュに必要な情報がある場合は認証情報を再取得する
  const updatedTokens = await AuthRepository.refreshAndSaveTokens({ refreshToken, username })

  // 認証情報の再取得に失敗した場合は認証情報を特に設定しない
  if (!updatedTokens) {
    return {
      headers: { ...headers, 'bearer-token': '' },
    }
  }

  return {
    headers: { ...headers, 'bearer-token': updatedTokens.accessToken },
  }
})

const createClient = () => {
  return new NextSSRApolloClient({
    link: ApolloLink.from([
      new SSRMultipartLink({ stripDefer: true }),
      new SentryLink(),
      authLink,
      onErrorLink,
      httpLink,
    ]),
    cache: new NextSSRInMemoryCache({
      typePolicies: {
        // プロフィール・社内ユーザー・社外ユーザーなど複数が持っていてキャッシュが意図しないタイミングで上書きされてしまう事があったため
        // __typename + id + role.id もセットでキャッシュする
        AssignedProject: {
          keyFields: ['id', 'role', ['id']],
        },
        Room: {
          // 読み込み順によってdiffRoomsのキャッシュがcachedRoomsによって上書きされる事があったため、キャッシュキーを変更し、それぞれ別の値としてキャッシュされるようにする
          keyFields: ['id', 'source'],
        },
        // 住戸一覧を取得する際に違う住戸でもuser.idが同じ場合、各フィールドが1回目に呼び出されたものがキャッシュされ2回目以降も1回目と同じ値になってしまうため定義
        RoomUser: {
          keyFields: [
            'id',
            'lastSelectEmailSentAt',
            'lastAgreementRequestEmailSentAt',
            'selectEmailSentTimes',
            'agreementRequestEmailSentTimes',
            'identification',
            ['code', 'status'],
          ],
        },
        AssingedUser: {
          // ユーザー詳細からプロジェクト詳細に遷移し、ブラウザバックした後、別プロジェクトを選択するとキャッシュが残って別プロジェクトのCompassRoleが表示されてしまうことがあるのを防ぐ
          // 参考: https://github.com/STYLE-PORT/roov-works-client/pull/1654
          keyFields: ['id', 'role', ['id']],
        },
        RoomType: {
          keyFields: ['id', 'name', 'worksRoomIds', 'compassRoomCount'],
        },
        CompanyRole: {
          keyFields: ['id', 'name', 'policyIds'],
        },
        CompanyPolicy: {
          keyFields: ['id', 'name', 'displayName'],
        },
        RoomSelectMaster: {
          keyFields: ['id', 'selectOptionMasterIds'],
        },
        Select: {
          keyFields: ['id', 'selectOptions'],
        },
        SelectRegistration: {
          keyFields: ['id', 'registeredSelectOption'],
        },
        RegisteredSelectOption: {
          keyFields: ['id', 'child'],
        },
      },
    }),
  })
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  return <ApolloNextAppProvider makeClient={createClient}>{children}</ApolloNextAppProvider>
}
